/**
 * WhistleblowingPage — Fase 7 "Strict A": NESSUN canale informatico.
 *
 * Cosa è cambiato (mag 2026):
 *  - Rimosso il form di segnalazione online (canale informatico)
 *  - Rimosso il check stato / messaggistica integrata
 *  - Aggiunto banner avviso in cima
 *  - Aggiunta sezione "Documenti scaricabili" con Policy + Procedura PDF
 *  - Mantenuti: informativa completa, ambiti, tutele, FAQ tassative,
 *    canale ANAC esterno, regolamento interno
 *
 * Documenti pubblicati: vedi /api/legal/whistleblowing/{policy,procedura}.pdf
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Shield, ArrowLeft, AlertTriangle, FileText, Download,
  Calendar, Clock, ExternalLink, Mail, Phone, MapPin, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// FASE 8 — Whistleblowing disclaimer aggiornato (mag 2026):
// - Gestore TEMPORANEO interno (in attesa di nomina di gestore esterno indipendente):
//   Christian Colucci, amministratore unico.
// - Si raccomanda canale ANAC esterno per maggiore garanzia di indipendenza
//   ai sensi della delibera ANAC 12/07/2023 per soggetti privati con <50 dipendenti.
// - Sede legale: ancora da compilare (placeholder).
// - Data adozione: data odierna del deploy.
// ============================================================================
const PLACEHOLDER = {
  // DA COMPILARE PRIMA DELLA PUBBLICAZIONE UFFICIALE — manca indirizzo sede legale
  SEDE_LEGALE: "[INDIRIZZO_SEDE_LEGALE_DA_COMPILARE]",
  GESTORE: "Christian Colucci, amministratore unico (gestore temporaneo)",
  CENTRALINO: "081 761 1000",
  DATA_ADOZIONE: new Date().toLocaleDateString("it-IT"),
};

export default function WhistleblowingPage() {
  const [mode, setMode] = useState("info");
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    axios.get(`${API}/legal/whistleblowing/info`).then((r) => setDocs(r.data)).catch(() => setDocs({ documents: {} }));
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="whistleblowing-page">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 py-12">
          <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1 mb-6" data-testid="wb-back-home">
            <ArrowLeft className="w-4 h-4" /> Torna alla Home
          </Link>

          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 bg-[#163321]/10 rounded-2xl flex items-center justify-center shrink-0">
              <Shield className="w-7 h-7 text-[#163321]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-medium text-[#0F1F14] leading-tight">Canale Whistleblowing</h1>
              <p className="text-sm text-[#4A5E51] mt-1">Segnalazioni protette ex <strong>D.Lgs. 24/2023</strong> (attuazione Direttiva UE 2019/1937) e <strong>Linee Guida ANAC / ANIA</strong></p>
            </div>
          </div>

          {/* BANNER AVVISO STRICT-A */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="wb-banner-strict-a">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <strong className="block mb-1">Modalità di segnalazione attive</strong>
              Per inviare una segnalazione, utilizza il <strong>canale scritto</strong> (raccomandata A/R con doppia busta) o richiedi un <strong>incontro diretto</strong> con il Gestore.
              Non sono attualmente disponibili canali informatici (online), come previsto dalle indicazioni ANIA / ANAC per intermediari di piccole dimensioni che gestiscono volumi limitati di segnalazioni.
            </div>
          </div>

          {/* TABS — solo info, documenti, canali ufficiali, canali esterni */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E5E2DB]">
            {[
              { id: "info", label: "Informativa" },
              { id: "documenti", label: "Documenti scaricabili" },
              { id: "canali_ufficiali", label: "Canali di segnalazione" },
              { id: "canali_esterni", label: "Canali Esterni (ANAC)" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setMode(t.id)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${mode === t.id ? "border-[#163321] text-[#163321]" : "border-transparent text-[#4A5E51] hover:text-[#163321]"}`}
                data-testid={`wb-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ========= INFORMATIVA ========= */}
          {mode === "info" && (
            <div className="space-y-6 text-[#4A5E51] text-sm leading-relaxed" data-testid="wb-info-content">
              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Titolare del Canale</h2>
                <p><strong>Colucci Assicuratori S.A.S. di Christian Colucci</strong> — RUI A000464752 — P.IVA 07618011212.</p>
                <p className="mt-2"><strong>Gestore del Canale:</strong> {PLACEHOLDER.GESTORE} (soggetto designato con atto formale dell'agenzia, tenuto al segreto d'ufficio e unico autorizzato alla lettura delle segnalazioni).</p>
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 leading-relaxed">
                  <strong className="block mb-1">Avviso di trasparenza</strong>
                  In attesa della nomina formale di un Gestore esterno indipendente (in fase di valutazione), il ruolo è temporaneamente svolto dall'amministratore unico. <strong>Si raccomanda di privilegiare il canale ANAC esterno</strong> per maggiore garanzia di indipendenza, in conformità alle indicazioni <strong>ANAC delibera 12/07/2023</strong> per soggetti privati con meno di 50 dipendenti.
                </div>
                <p className="mt-3">
                  <Link to="/whistleblowing/regolamento" className="text-[#163321] underline hover:no-underline inline-flex items-center gap-1" data-testid="wb-link-regolamento">
                    <Download className="w-3.5 h-3.5" /> Scarica il Regolamento Whistleblowing completo
                  </Link>
                </p>
              </div>

              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Tempi normativi di riscontro</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-4 bg-[#F5F8F6] rounded-xl border border-[#163321]/20">
                    <Clock className="w-5 h-5 text-[#163321] mb-2" />
                    <p className="text-sm font-medium text-[#0F1F14]">7 giorni</p>
                    <p className="text-xs text-[#4A5E51] mt-1">Avviso di ricevimento della segnalazione (art. 5 c.1 lett. a)</p>
                  </div>
                  <div className="p-4 bg-[#F5F8F6] rounded-xl border border-[#163321]/20">
                    <Calendar className="w-5 h-5 text-[#163321] mb-2" />
                    <p className="text-sm font-medium text-[#0F1F14]">3 mesi</p>
                    <p className="text-xs text-[#4A5E51] mt-1">Riscontro sull'esito delle verifiche (art. 5 c.1 lett. d)</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Tutele per il segnalante (art. 12 e 17)</h2>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Riservatezza assoluta</strong> dell'identità del segnalante, delle persone coinvolte e del contenuto</li>
                  <li><strong>Divieto di ritorsione</strong>: nulli licenziamenti, demansionamenti, trasferimenti, molestie o discriminazioni per il fatto di aver segnalato</li>
                  <li><strong>Inversione dell'onere della prova</strong>: spetta al datore di lavoro dimostrare che una misura sfavorevole non è legata alla segnalazione</li>
                  <li><strong>Segnalazione anonima</strong> ammessa anche tramite canale scritto (busta interna sigillata)</li>
                  <li><strong>Protezione GDPR</strong> (Reg. UE 2016/679): nessun dato personale trattato oltre lo stretto necessario</li>
                  <li><strong>Esonero responsabilità</strong> civile, penale e disciplinare se la segnalazione è fatta in buona fede (art. 20)</li>
                </ul>
              </div>

              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Cosa si può segnalare (art. 2)</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Illeciti amministrativi, contabili, civili o penali</li>
                  <li>Condotte corruttive, frodi, appropriazione indebita</li>
                  <li>Violazioni del D.Lgs. 231/2001 sulla responsabilità amministrativa degli enti</li>
                  <li>Violazioni normativa IVASS, antiriciclaggio (D.Lgs. 231/2007)</li>
                  <li>Violazioni GDPR e trattamento illecito di dati personali</li>
                  <li>Molestie, discriminazioni, mobbing, violazioni del Codice Etico</li>
                  <li>Violazioni della sicurezza sul lavoro (D.Lgs. 81/2008)</li>
                  <li>Violazioni ambientali, sanità pubblica e sicurezza alimentare</li>
                  <li>Violazioni in materia di concorrenza, antitrust, servizi finanziari</li>
                </ul>
              </div>

              <div className="bg-[#FFF7E8] border border-[#E8D3A0] rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#B8820E] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading text-sm text-[#8B6100] font-semibold mb-2">Segnalazioni NON ammesse</h3>
                    <p className="text-xs text-[#8B6100]">Lamentele personali, rivendicazioni private, segnalazioni manifestamente infondate, diffamatorie o fatte in <strong>mala fede</strong>. Ai sensi dell'art. 16 D.Lgs. 24/2023, in questi casi il segnalante <strong>perde le tutele</strong> e può rispondere penalmente (calunnia, diffamazione) e civilmente.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#163321] text-white rounded-2xl p-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="font-heading text-lg font-medium mb-1">Vuoi segnalare?</h3>
                  <p className="text-sm text-white/70">Vai alla sezione "Canali di segnalazione" per le modalità ufficiali.</p>
                </div>
                <button onClick={() => setMode("canali_ufficiali")} className="bg-white text-[#163321] hover:bg-white/90 rounded-full px-6 h-11 font-semibold text-sm" data-testid="wb-cta-go-canali">
                  Vai ai canali ufficiali
                </button>
              </div>
            </div>
          )}

          {/* ========= DOCUMENTI ========= */}
          {mode === "documenti" && (
            <div className="space-y-6" data-testid="wb-documenti-content">
              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <FileText className="w-6 h-6 text-[#163321] shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-heading text-xl text-[#0F1F14] font-medium">Documenti scaricabili</h2>
                    <p className="text-sm text-[#4A5E51] mt-1">Scarica la Policy e la Procedura interna in formato PDF.</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <DocumentCard
                    docKey="policy"
                    title="Policy Whistleblowing"
                    description="Documento di policy adottato dall'agenzia ai sensi del D.Lgs. 24/2023."
                    docs={docs}
                  />
                  <DocumentCard
                    docKey="procedura"
                    title="Procedura Whistleblowing"
                    description="Indicazioni operative interne per la gestione delle segnalazioni."
                    docs={docs}
                  />
                </div>

                <div className="mt-5 bg-[#F5F4F0] rounded-xl p-4 text-xs text-[#4A5E51] leading-relaxed">
                  <p><strong>Versione:</strong> Policy adottata il <strong>{PLACEHOLDER.DATA_ADOZIONE}</strong>. Documenti firmati e archiviati presso la sede legale dell'agenzia. Eventuali aggiornamenti normativi saranno pubblicati su questa pagina.</p>
                  <p className="mt-1.5 text-[10px] text-[#8B6100]">Nota interna: indirizzo sede legale ancora da formalizzare nel testo definitivo della Policy. <em>DA COMPILARE PRIMA DELLA PUBBLICAZIONE UFFICIALE.</em></p>
                </div>
              </div>
            </div>
          )}

          {/* ========= CANALI UFFICIALI ========= */}
          {mode === "canali_ufficiali" && (
            <div className="space-y-6" data-testid="wb-canali-ufficiali-content">
              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-2">Canali di segnalazione attivi</h2>
                <p className="text-sm text-[#4A5E51] mb-5">Sono attivi <strong>due canali ufficiali</strong> conformi all'art. 4 D.Lgs. 24/2023. La scelta è libera; entrambi garantiscono la massima riservatezza.</p>

                <div className="space-y-4">
                  {/* Canale 1: Raccomandata */}
                  <div className="bg-[#F5F8F6] border border-[#163321]/20 rounded-2xl p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#163321]/15 flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-[#163321]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-medium text-[#0F1F14]">Canale scritto — Raccomandata A/R con doppia busta</h3>
                        <p className="text-xs text-[#4A5E51]">Modalità preferita per segnalazioni anonime e non anonime.</p>
                      </div>
                    </div>
                    <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[#4A5E51]">
                      <li>Inserisci il <strong>contenuto della segnalazione</strong> (e l'eventuale documentazione) in una <strong>busta interna sigillata</strong>.</li>
                      <li>Se vuoi rimanere identificato, inserisci in una <strong>seconda busta interna</strong> i tuoi dati e copia di un documento d'identità. Se vuoi restare anonimo, ometti questa busta.</li>
                      <li>Inserisci entrambe le buste in una <strong>terza busta esterna</strong>, su cui scriverai la dicitura: <em>"Riservata al Gestore delle Segnalazioni Whistleblowing"</em>.</li>
                      <li>Invia tramite <strong>raccomandata A/R</strong> al seguente indirizzo:</li>
                    </ol>
                    <div className="bg-white border border-[#163321]/30 rounded-xl p-3 mt-3 font-mono text-sm text-[#0F1F14]" data-testid="wb-indirizzo-raccomandata">
                      <strong>Colucci Assicuratori S.A.S. di Christian Colucci</strong><br />
                      All'attenzione del <strong>Gestore Whistleblowing</strong><br />
                      {PLACEHOLDER.SEDE_LEGALE}
                    </div>
                  </div>

                  {/* Canale 2: Incontro */}
                  <div className="bg-[#F5F8F6] border border-[#163321]/20 rounded-2xl p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-[#163321]/15 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5 text-[#163321]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-medium text-[#0F1F14]">Incontro diretto con il Gestore</h3>
                        <p className="text-xs text-[#4A5E51]">Modalità orale ex art. 4 c.2 D.Lgs. 24/2023.</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#4A5E51] mb-3">
                      Puoi richiedere un incontro diretto con il Gestore. L'incontro verrà fissato entro un termine ragionevole presso la sede dell'agenzia, in luogo riservato. Su tuo consenso, potrà essere documentato tramite registrazione o verbale sottoscritto da entrambi.
                    </p>
                    <div className="space-y-2 text-sm">
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#163321]" /> <strong>Centralino:</strong>
                        <a href={`tel:${PLACEHOLDER.CENTRALINO.replace(/\s/g, "")}`} className="text-[#163321] underline">{PLACEHOLDER.CENTRALINO}</a>
                      </p>
                      <p className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#163321]" /> <strong>Sede:</strong> {PLACEHOLDER.SEDE_LEGALE}
                      </p>
                      <p className="text-xs text-[#4A5E51] italic mt-2">
                        Indica al centralino di voler richiedere un incontro per "segnalazione whistleblowing": la richiesta verrà inoltrata al Gestore senza trattenerne copia.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FFF7E8] border border-[#E8D3A0] rounded-2xl p-5 text-xs text-[#8B6100] leading-relaxed">
                <p className="flex items-start gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Questa configurazione (no canale informatico) è coerente con le indicazioni ANIA/ANAC per intermediari assicurativi di piccole dimensioni che gestiscono volumi limitati di segnalazioni e dispongono di un perimetro organizzativo contenuto.</span>
                </p>
              </div>
            </div>
          )}

          {/* ========= CANALI ESTERNI (ANAC) ========= */}
          {mode === "canali_esterni" && (
            <div className="space-y-6" data-testid="wb-canali-esterni-content">
              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Canale esterno ANAC (art. 6)</h2>
                <p className="text-sm text-[#4A5E51]">Ai sensi dell'<strong>art. 6 D.Lgs. 24/2023</strong>, puoi rivolgerti direttamente all'<strong>Autorità Nazionale Anticorruzione (ANAC)</strong> nei seguenti casi:</p>
                <ul className="list-disc pl-5 mt-3 space-y-1 text-sm text-[#4A5E51]">
                  <li>Il canale interno non è attivo o non conforme</li>
                  <li>Hai già segnalato internamente senza riscontro</li>
                  <li>Hai fondato motivo di ritenere che la segnalazione interna non avrebbe seguito efficace o determinerebbe ritorsioni</li>
                  <li>La violazione costituisce <strong>pericolo imminente e manifesto</strong> per il pubblico interesse</li>
                </ul>
                <a
                  href="https://www.anticorruzione.it/-/whistleblowing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-5 h-11 bg-[#163321] text-white rounded-full hover:bg-[#1d4a2e] transition-colors font-semibold text-sm"
                  data-testid="wb-link-anac"
                >
                  <ExternalLink className="w-4 h-4" /> Portale ANAC Whistleblowing
                </a>
              </div>

              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6 text-sm text-[#4A5E51]">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Divulgazione pubblica (art. 15)</h2>
                <p>La divulgazione pubblica è ammessa solo se ricorre <strong>almeno una</strong> delle seguenti condizioni tassative:</p>
                <ul className="list-disc pl-5 mt-3 space-y-1">
                  <li>Hai già effettuato segnalazione interna ed esterna senza riscontro</li>
                  <li>Hai fondato motivo di ritenere che la violazione possa costituire <strong>pericolo imminente o palese</strong> per il pubblico interesse</li>
                  <li>Hai fondato motivo di ritenere che la segnalazione esterna possa comportare rischio di <strong>ritorsioni</strong> o non avere efficace seguito</li>
                </ul>
                <p className="mt-3 text-xs text-[#8B6100] bg-[#FFF7E8] border border-[#E8D3A0] rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Al di fuori di questi casi, la divulgazione pubblica fa <strong>perdere le tutele</strong> previste dalla normativa e può configurare responsabilità civile/penale.
                </p>
              </div>

              <div className="bg-white border border-[#E5E2DB] rounded-2xl p-6 text-sm text-[#4A5E51]">
                <h2 className="font-heading text-lg text-[#0F1F14] font-medium mb-3">Informativa Privacy ex art. 13 GDPR</h2>
                <p>Trattiamo i dati conferiti nella segnalazione esclusivamente per:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Gestire e istruire la segnalazione</li>
                  <li>Dare riscontro al segnalante (se identificato)</li>
                  <li>Adempiere agli obblighi di legge (D.Lgs. 24/2023, GDPR)</li>
                </ul>
                <p className="mt-3"><strong>Base giuridica:</strong> obbligo legale (art. 6.1.c GDPR) e interesse pubblico rilevante (art. 9.2.g GDPR).</p>
                <p className="mt-2"><strong>Conservazione:</strong> tempo strettamente necessario, comunque non oltre <strong>5 anni</strong> dalla comunicazione dell'esito finale (art. 14 D.Lgs. 24/2023).</p>
                <p className="mt-2"><strong>Destinatari:</strong> esclusivamente il Gestore del Canale designato; eventualmente ANAC o Autorità Giudiziaria se la segnalazione configura reato.</p>
                <p className="mt-2"><strong>Diritti:</strong> accesso, rettifica, cancellazione, limitazione, opposizione e portabilità (artt. 15-22 GDPR), con le limitazioni previste dall'art. 2-undecies del D.Lgs. 196/2003 per garantire la riservatezza del segnalante e dei segnalati.</p>
                <p className="mt-2"><strong>Responsabile Protezione Dati (DPO):</strong> per l'esercizio dei diritti GDPR contatta il nostro DPO. Vedi <Link to="/privacy/dpo" className="text-[#163321] underline">contatti e informazioni DPO</Link> (art. 37 GDPR).</p>
                <p className="mt-2"><strong>Reclami:</strong> Garante Privacy — <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">www.garanteprivacy.it</a></p>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function DocumentCard({ docKey, title, description, docs }) {
  const meta = docs?.documents?.[docKey];
  const url = meta?.available ? `${process.env.REACT_APP_BACKEND_URL}${meta.url}` : null;
  return (
    <div className="bg-[#F5F8F6] border border-[#163321]/20 rounded-2xl p-5 flex flex-col">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#163321]/15 flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-[#163321]" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-medium text-[#0F1F14]">{title}</h3>
          <p className="text-xs text-[#4A5E51] mt-0.5">{description}</p>
        </div>
      </div>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 h-10 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] transition-colors mt-auto"
          data-testid={`wb-download-${docKey}`}
        >
          <Download className="w-4 h-4" /> Scarica PDF
        </a>
      ) : (
        <button disabled className="inline-flex items-center justify-center gap-2 px-4 h-10 bg-[#E5E2DB] text-[#4A5E51] text-sm font-semibold rounded-full mt-auto cursor-not-allowed">
          <Download className="w-4 h-4" /> In aggiornamento
        </button>
      )}
      {meta?.available && (
        <p className="text-[10px] text-[#4A5E51] text-center mt-2">PDF · {Math.round((meta.size_bytes || 0) / 1024)} KB</p>
      )}
    </div>
  );
}
